import { QueryClient, UseQueryOptions, useQuery } from '@tanstack/react-query';

import { workspaceKeys } from '~teacher/hook/workspaces/workspaceQueryKeyFactory';

import { getWorkspace } from '../../../api/workspaces/getWorkspace';
import { Workspace } from '../../../api/workspaces/types';

type UseWorkspaceOptions<T> = UseQueryOptions<
  Workspace,
  unknown,
  T,
  ReturnType<typeof workspaceKeys.details>
>;

interface DefaultWorkspaceOptionsParams {
  workspaceId: string;
}

const DEFAULT_WORKSPACE_DETAIL_STALE_TIME = 30 * 1000; // 30 Seconds
function getDefaultWorkspaceOptions<T>({
  workspaceId,
}: DefaultWorkspaceOptionsParams): UseWorkspaceOptions<T> {
  return {
    queryKey: workspaceKeys.details(workspaceId),
    queryFn: () => getWorkspace(workspaceId),
    staleTime: DEFAULT_WORKSPACE_DETAIL_STALE_TIME,
  };
}

interface UseGetWorkspaceDetailParams<T = Workspace> {
  workspaceId: string;
  options?: UseWorkspaceOptions<T>;
}

export function useGetWorkspaceDetail<T = Workspace>({
  workspaceId,
  options = {},
}: UseGetWorkspaceDetailParams<T>) {
  return useQuery({
    ...getDefaultWorkspaceOptions<T>({ workspaceId: workspaceId }),
    ...options,
  });
}

interface FetchWorkspaceParams<T = Workspace> {
  workspaceId: string;
  queryClient: QueryClient;
  options?: UseWorkspaceOptions<T>;
}

export function fetchWorkspace<T = Workspace>({
  workspaceId,
  queryClient,
  options = {},
}: FetchWorkspaceParams<T>) {
  return queryClient.fetchQuery({
    ...getDefaultWorkspaceOptions<T>({ workspaceId }),
    ...options,
  });
}
